/* eslint-disable no-param-reassign */
import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Modal } from "antd";
import Message from "../../components/Message/Message";
import rtkRequestStatusCodes from "../../lib/const/rtkRequestStatusCodes";
import {
  getAccountByIdRoute,
  getAccountEmailVersionsRoute,
  getAccountPageableSearchRoute,
  getAccountSelectSearchRoute,
  getAccountsRoute,
  getAccountSubscribersRoute,
  getAccountVersionsRoute,
  getAddressesByAddressIdRoute,
  getAddressesVersionsRoute,
  getEmailsByAccountId,
  getOrganizationRoute,
  getOrganizationVersionsRoute,
} from "../../lib/routes/accounts";
import { getAccountPhonesVersionsRoute } from "../../lib/routes/accountsTs";
import {
  getDicVersionsRoute,
  getPropertiesRoute,
  getPropertiesVersionsRoute,
  patchPropertyRoute,
} from "../../lib/routes/properties";
import LocalStorageService from "../../services/LocalStorageService";
import { catchApiError } from "../../lib/utils/catchApiError";

const initialState = {
  accounts: [],
  account: {},
  accountOrganization: {},
  accountOrganizationLoading: rtkRequestStatusCodes.idle,
  accountStatus: {},
  accountStatusLoading: rtkRequestStatusCodes.idle,
  accountDirectProperties: {}, // Изначально был сделан неправильный нейминг accountProperties - которые фактически свойства организации, т.к. берутся из organization.properties. Поэтому для свойств аккаунта пришлось изголяться
  accountDirectPropertiesLoading: rtkRequestStatusCodes.idle,
  accountProperties: {},
  accountPropertiesLoading: rtkRequestStatusCodes.idle,
  accountTypePropertyVersions: [],
  accountTypePropertyVersionsLoading: rtkRequestStatusCodes.idle,
  accountAddresses: {},
  accountAddressesLoading: rtkRequestStatusCodes.idle,
  accountsVersions: [],
  accountsVersionsLoading: rtkRequestStatusCodes.idle,
  accountEmails: [],
  accountEmailsLoading: rtkRequestStatusCodes.idle,
  accountSubscribers: [],
  accountSubscribersLoading: rtkRequestStatusCodes.idle,
  accountIdentityDocuments: {},
  accountIdentityDocumentsLoading: rtkRequestStatusCodes.idle,
  organizationsVersions: [],
  organizationsVersionsLoading: rtkRequestStatusCodes.idle,
  emailsVersions: [],
  emailsVersionsLoading: rtkRequestStatusCodes.idle,
  legalAddressesVersions: [],
  legalAddressesVersionsLoading: rtkRequestStatusCodes.idle,
  actualAddressesVersions: [],
  actualAddressesVersionsLoading: rtkRequestStatusCodes.idle,
  propertyVersions: [],
  propertyVersionsLoading: rtkRequestStatusCodes.idle,
  accDetailsId: 5282,
  accountsLoading: rtkRequestStatusCodes.idle,
  accountLoading: rtkRequestStatusCodes.idle,
  accountForBlockingLoading: rtkRequestStatusCodes.idle,
  accountUpdating: rtkRequestStatusCodes.idle,
  updatingAccountStatus: rtkRequestStatusCodes.idle,
  updatingRequisites: rtkRequestStatusCodes.idle,
  updatingLegalAddress: rtkRequestStatusCodes.idle,
  updatingActualAddress: rtkRequestStatusCodes.idle,
  accountSelect: "",
  accountSelectLoading: rtkRequestStatusCodes.idle,
  totalPages: 0,
  isPageableSearch: false,
  accountPhones: [],
  accountPhonesLoading: rtkRequestStatusCodes.idle,
  accountPhonesVersions: [],
  accountPhonesVersionsLoading: rtkRequestStatusCodes.idle,
  accountIndividual: {},
  accountIndividualLoading: rtkRequestStatusCodes.idle,
};

const fetchAccounts = createAsyncThunk("accounts/fetchAll", async ({ page }) => {
  const response = axios.get(getAccountsRoute(page));
  const { data } = await response;
  return data;
});

const fetchAccountOrganization = createAsyncThunk(
  "accounts/fetchAccountOrganization",
  async ({ orgId }) => {
    const response = axios.get(getOrganizationRoute(orgId));
    const { data } = await response;
    return data;
  }
);

const fetchAccountEmails = createAsyncThunk(
  "accounts/fetchAccountEmails",
  async ({ accountId }) => {
    const response = axios.get(getEmailsByAccountId(accountId));
    const { data } = await response;
    return data;
  }
);

const fetchAccountById = createAsyncThunk("accounts/fetchAccountById", async ({ id }) => {
  const response = axios.get(getAccountByIdRoute(id));
  const { data } = await response;
  return data;
});

const fetchAccountByIdForBlocking = createAsyncThunk(
  "accounts/fetchAccountByIdForBlocking",
  async ({ id }) => {
    const response = axios.get(getAccountByIdRoute(id));
    const { data } = await response;
    return data;
  }
);

const fetchAccountPropertiesByOrgId = createAsyncThunk(
  "accounts/fetchAccountProperties",
  async (orgId) => {
    const response = axios.get(
      getPropertiesRoute({ objectTypeSysname: "organization", objectId: orgId })
    );
    const { data } = await response;
    return data;
  }
);

const fetchAccountAddressesByAddIds = createAsyncThunk(
  "accounts/fetchAccountAddresses",
  async ({ legalAddressId, actualAddressId }) => {
    const legalAddressResponse = axios.get(getAddressesByAddressIdRoute(legalAddressId));
    const actualAddressResponse = axios.get(getAddressesByAddressIdRoute(actualAddressId));
    const { data: legalAddress } = await legalAddressResponse;
    const { data: actualAddress } = await actualAddressResponse;
    return { legalAddress, actualAddress };
  }
);

const fetchAccountStatusById = createAsyncThunk(
  "accounts/fetchAccountStatusById",
  async ({ id }) => {
    const response = axios.get(`${getAccountByIdRoute(id)}?simpleView=true`);
    const { data } = await response;
    return data;
  }
);

const fetchAccountPageableSearch = createAsyncThunk(
  "accounts/pageableSearch",
  async ({ searchParam, page = 0, itemsPerPage = 10 }) => {
    const params = {};
    Object.keys(searchParam).forEach((k) => {
      if (searchParam[k]) {
        if (k === "organization.property.stringValue") {
          params["organization.property.typeId"] =
            LocalStorageService.getTenantId() === "1" ? "BIN" : "INN";
        }
        params[k] = searchParam[k];
      }
    });

    const response = axios.get(getAccountPageableSearchRoute(), {
      params: {
        ...params,
        page,
        size: itemsPerPage,
      },
    });
    const { data } = await response;
    return data;
  }
);

const fetchAccountsByName = createAsyncThunk(
  "accounts/fetchAccountsByName",
  async ({ searchParam, page = 0, itemsPerPage = 20 }) => {
    const response = axios.get(getAccountSelectSearchRoute(searchParam, page, itemsPerPage));
    const { data } = await response;
    return data;
  }
);

const getSubscribers = createAsyncThunk("accounts/getSubscribers", async ({ accountId }) => {
  const response = axios.get(getAccountSubscribersRoute(accountId));
  const { data } = await response;
  return data;
});

const getOrganizationsVersions = createAsyncThunk(
  "accounts/getOrganizationsVersions",
  async ({ organizationsIds }) => {
    const response = Promise.all(
      organizationsIds.map((organizationId) =>
        axios.get(getOrganizationVersionsRoute(organizationId))
      )
    );
    const data = await response;
    return data.map((item) => item.data);
  }
);

const getEmailsVersions = createAsyncThunk("accounts/getEmailsVersions", async ({ emailsIds }) => {
  const request = Promise.all(
    emailsIds.map((emailId) => axios.get(getAccountEmailVersionsRoute(emailId)))
  );
  const res = await request;
  return res.map((item) => item.data);
});

const getPropertiesVersions = createAsyncThunk(
  "accounts/getPropertiesVersions",
  async ({ propertiesArr, propType }) => {
    const request = Promise.all(
      propertiesArr.map((propId) =>
        axios.get(
          getDicVersionsRoute({
            objectTypeSysname: "organization",
            objectId: propId,
            propertyTypeAlias: propType,
          })
        )
      )
    );
    const res = await request;
    return res.map((item) => item.data);
  }
);

const getAccountsVersions = createAsyncThunk(
  "accounts/getAccountsVersions",
  async ({ accountsIds }) => {
    const response = Promise.all(
      accountsIds.map((accId) => axios.get(getAccountVersionsRoute(accId)))
    );
    const data = await response;
    return data.map((item) => item.data);
  }
);

const getLegalAddressVersions = createAsyncThunk(
  "accounts/getLegalAddressVersions",
  async ({ addressesIds }) => {
    const response = Promise.all(
      addressesIds.map((accId) => axios.get(getAddressesVersionsRoute(accId)))
    );
    const data = await response;
    return data.map((item) => item.data);
  }
);

const getActualAddressVersions = createAsyncThunk(
  "accounts/getActualAddressVersions",
  async ({ addressesIds }) => {
    const response = Promise.all(
      addressesIds.map((accId) => axios.get(getAddressesVersionsRoute(accId)))
    );
    const data = await response;
    return data.map((item) => item.data);
  }
);

const fetchAccountPhonesVersions = createAsyncThunk(
  "accounts/fetchAccountPhonesVersions",
  (phoneId) =>
    axios
      .get(getAccountPhonesVersionsRoute(phoneId))
      .then(({ data }) => data)
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("Ошибка загрузки версий телефонных номеров:", e.message);
        throw Error(e.message);
      })
);

// Отдельного метода нет, поэтому используется getAccountById
export const fetchAccountPhones = createAsyncThunk("accounts/fetchAccountPhones", ({ accountId }) =>
  axios
    .get(getAccountByIdRoute(accountId))
    .then(({ data }) => data)
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.error("Ошибка загрузки данных аккаунта:", e.message);
      throw Error(e.message);
    })
);

// Отдельного метода нет, поэтому используется getAccountById
export const fetchIndividualByAccountId = createAsyncThunk(
  "accounts/fetchIndividualByAccountId",
  ({ accountId }) =>
    axios
      .get(getAccountByIdRoute(accountId))
      .then(({ data }) => data)
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("Ошибка загрузки данных аккаунта:", e.message);
        throw Error(e.message);
      })
);

// Отдельного метода нет, поэтому используется getAccountById
export const fetchIndividualDocument = createAsyncThunk(
  "accounts/fetchIndividualDocument",
  ({ accountId }) =>
    axios
      .get(getAccountByIdRoute(accountId))
      .then(({ data }) => data)
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("Ошибка загрузки данных аккаунта:", e.message);
        throw Error(e.message);
      })
);

// Получение свойств аккаунта
export const fetchAccountDirectProperties = createAsyncThunk(
  "offering/fetchAccountDirectProperties",
  async (accountId) => {
    try {
      const response = axios.get(
        getPropertiesRoute({ objectTypeSysname: "account", objectId: accountId })
      );
      const { data } = await response;
      // Modal.success({
      //   title: `Версия профиля ${force ? "обновлена" : "создана"}`,
      // });
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки свойств аккаунта. Попробуйте позже",
      });
    }
  }
);

// Получение версий свойства аккаунта: "Тип аккаунта"
export const fetchAccountTypePropertyVersions = createAsyncThunk(
  "accounts/fetchAccountTypePropertyVersions",
  async (propertyId) => {
    try {
      const response = axios.get(getPropertiesVersionsRoute(propertyId));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки версий типа аккаунта. Попробуйте позже",
      });
    }
  }
);

// Обновление свойства аккаунта, последующая загрузка свойств аккаунта и версий свойства "Тип аккаунта"
export const updateAccountProperty = createAsyncThunk(
  "offering/updateAccountProperty",
  async ({ accountId, force, property }, { dispatch }) => {
    try {
      const response = axios.patch(
        patchPropertyRoute({ id: property.id, fromDate: property.fromDate, force }),
        property
      );
      const { data } = await response;
      await Promise.all([
        dispatch(fetchAccountDirectProperties(accountId)),
        dispatch(fetchAccountTypePropertyVersions(property.id)),
      ]).then(() => {
        Modal.success({
          title: `Версия свойства ${force ? "обновлена" : "создана"}`,
        });
      });
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка обновления свойства. Попробуйте позже",
      });
    }
  }
);

export const accountsSlice = createSlice({
  name: "accounts",
  initialState,
  reducers: {
    selectAccId: {
      reducer: (state, action) => {
        state.accDetailsId = action.payload.accId;
      },
    },
    setIsPageableSearch: {
      reducer: (state, action) => {
        state.isPageableSearch = action.payload;
      },
    },
    cleanAccountSelect: {
      reducer: (state) => {
        state.accountSelect = "";
        state.accountSelectLoading = rtkRequestStatusCodes.idle;
      },
    },
  },
  extraReducers: {
    [fetchAccounts.fulfilled]: (state, action) => {
      state.accounts = action.payload.content;
      state.totalPages = action.payload.totalPages;
      state.accountsLoading = rtkRequestStatusCodes.fulfilled;
      state.isPageableSearch = false;
    },
    [fetchAccounts.pending]: (state) => {
      state.accountsLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountById.fulfilled]: (state, action) => {
      state.account = action.payload;
      state.accountLoading = rtkRequestStatusCodes.fulfilled;
      const isIndividual = action.payload.individual !== null;
      if (isIndividual) {
        state.accountSubscribers = action.payload.subscribers;
        state.accountSubscribersLoading = rtkRequestStatusCodes.fulfilled;
        state.accountStatus = action.payload;
        state.accountStatusLoading = rtkRequestStatusCodes.fulfilled;
        state.accountIdentityDocuments = action.payload.individual.identityDocuments;
        state.accountIdentityDocumentsLoading = rtkRequestStatusCodes.fulfilled;
        state.accountEmails = action.payload.billDeliveryEmails;
        state.accountEmailsLoading = rtkRequestStatusCodes.fulfilled;
        state.accountPhones = action.payload.individual.phones;
        state.accountPhonesLoading = rtkRequestStatusCodes.fulfilled;
        state.accountIndividual = action.payload.individual;
        state.accountIndividualLoading = rtkRequestStatusCodes.fulfilled;
        state.accountAddresses = {
          legalAddress: action.payload.individual.registrationAddress,
          actualAddress: action.payload.individual.actualAddress,
        };
        state.accountAddressesLoading = rtkRequestStatusCodes.fulfilled;
      } else {
        state.accountAddresses = {
          legalAddress: action.payload.organization.legalAddress,
          actualAddress: action.payload.organization.actualAddress,
        };
        state.accountAddressesLoading = rtkRequestStatusCodes.fulfilled;
        state.accountEmails = action.payload.billDeliveryEmails;
        state.accountEmailsLoading = rtkRequestStatusCodes.fulfilled;
        state.accountPhones = action.payload.organization.phones;
        state.accountPhonesLoading = rtkRequestStatusCodes.fulfilled;
        state.accountSubscribers = action.payload.subscribers;
        state.accountSubscribersLoading = rtkRequestStatusCodes.fulfilled;
        state.accountProperties = action.payload.organization.properties;
        state.accountPropertiesLoading = rtkRequestStatusCodes.fulfilled;
        state.accountDirectProperties = action.payload.properties;
        state.accountDirectPropertiesLoading = rtkRequestStatusCodes.fulfilled;
        state.accountOrganization = action.payload.organization;
        state.accountOrganizationLoading = rtkRequestStatusCodes.fulfilled;
        state.accountStatus = action.payload;
        state.accountStatusLoading = rtkRequestStatusCodes.fulfilled;
      }
    },
    [fetchAccountById.pending]: (state) => {
      state.accountLoading = rtkRequestStatusCodes.pending;
      state.accountAddressesLoading = rtkRequestStatusCodes.pending;
      state.accountEmailsLoading = rtkRequestStatusCodes.pending;
      state.accountSubscribersLoading = rtkRequestStatusCodes.pending;
      state.accountPropertiesLoading = rtkRequestStatusCodes.pending;
      state.accountOrganizationLoading = rtkRequestStatusCodes.pending;
      state.accountStatusLoading = rtkRequestStatusCodes.pending;
      state.accountIdentityDocumentsLoading = rtkRequestStatusCodes.pending;
      state.accountPhonesLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountById.rejected]: (state) => {
      state.accountLoading = rtkRequestStatusCodes.rejected;
      state.accountAddressesLoading = rtkRequestStatusCodes.rejected;
      state.accountEmailsLoading = rtkRequestStatusCodes.rejected;
      state.accountSubscribersLoading = rtkRequestStatusCodes.rejected;
      state.accountPropertiesLoading = rtkRequestStatusCodes.rejected;
      state.accountOrganizationLoading = rtkRequestStatusCodes.rejected;
      state.accountStatusLoading = rtkRequestStatusCodes.rejected;
      state.accountIdentityDocumentsLoading = rtkRequestStatusCodes.rejected;
      state.accountPhonesLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountByIdForBlocking.fulfilled]: (state, action) => {
      if (action.payload?.blockingStatus) {
        state.account.blockingStatus = action.payload.blockingStatus;
      }
      state.accountForBlockingLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchAccountByIdForBlocking.pending]: (state) => {
      state.accountForBlockingLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountByIdForBlocking.rejected]: (state) => {
      state.accountForBlockingLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountPageableSearch.fulfilled]: (state, action) => {
      state.accounts = action.payload.content;
      state.totalPages = action.payload.totalPages;
      state.accountsLoading = rtkRequestStatusCodes.fulfilled;
      state.isPageableSearch = true;
    },
    [fetchAccountPageableSearch.pending]: (state) => {
      state.accountsLoading = rtkRequestStatusCodes.pending;
    },
    [getSubscribers.pending]: (state) => {
      state.accountSubscribersLoading = rtkRequestStatusCodes.pending;
    },
    [getSubscribers.fulfilled]: (state, { payload }) => {
      state.accountSubscribers = payload;
      state.accountSubscribersLoading = rtkRequestStatusCodes.fulfilled;
    },
    [getSubscribers.rejected]: (state, action) => {
      state.accountSubscribersLoading = rtkRequestStatusCodes.rejected;
      Message(action.error.message);
    },
    [getOrganizationsVersions.pending]: (state) => {
      state.organizationsVersionsLoading = rtkRequestStatusCodes.pending;
    },
    [getOrganizationsVersions.fulfilled]: (state, { payload }) => {
      state.organizationsVersions = payload;
      state.organizationsVersionsLoading = rtkRequestStatusCodes.fulfilled;
    },
    [getOrganizationsVersions.rejected]: (state, action) => {
      state.organizationsVersionsLoading = rtkRequestStatusCodes.rejected;
      Message(action.error.message);
    },
    [getAccountsVersions.pending]: (state) => {
      state.accountsVersionsLoading = rtkRequestStatusCodes.pending;
    },
    [getAccountsVersions.fulfilled]: (state, { payload }) => {
      state.accountsVersions = payload;
      state.accountsVersionsLoading = rtkRequestStatusCodes.fulfilled;
    },
    [getAccountsVersions.rejected]: (state, action) => {
      state.accountsVersionsLoading = rtkRequestStatusCodes.rejected;
      Message(action.error.message);
    },
    [getLegalAddressVersions.pending]: (state) => {
      state.legalAddressesVersionsLoading = rtkRequestStatusCodes.pending;
    },
    [getLegalAddressVersions.fulfilled]: (state, { payload }) => {
      state.legalAddressesVersions = payload;
      state.legalAddressesVersionsLoading = rtkRequestStatusCodes.fulfilled;
    },
    [getLegalAddressVersions.rejected]: (state, action) => {
      state.legalAddressesVersionsLoading = rtkRequestStatusCodes.rejected;
      Message(action.error.message);
    },
    [getActualAddressVersions.pending]: (state) => {
      state.actualAddressesVersionsLoading = rtkRequestStatusCodes.pending;
    },
    [getActualAddressVersions.fulfilled]: (state, { payload }) => {
      state.actualAddressesVersions = payload;
      state.actualAddressesVersionsLoading = rtkRequestStatusCodes.fulfilled;
    },
    [getActualAddressVersions.rejected]: (state, action) => {
      state.actualAddressesVersionsLoading = rtkRequestStatusCodes.rejected;
      Message(action.error.message);
    },
    [getPropertiesVersions.pending]: (state) => {
      state.propertyVersionsLoading = rtkRequestStatusCodes.pending;
    },
    [getPropertiesVersions.fulfilled]: (state, { payload }) => {
      state.propertyVersions = payload;
      state.propertyVersionsLoading = rtkRequestStatusCodes.fulfilled;
    },
    [getPropertiesVersions.rejected]: (state) => {
      state.propertyVersionsLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountsByName.fulfilled]: (state, action) => {
      state.accountSelect = action.payload;
      state.accountSelectLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchAccountsByName.pending]: (state) => {
      state.accountSelectLoading = rtkRequestStatusCodes.pending;
    },
    [getEmailsVersions.rejected]: (state) => {
      state.emailsVersionsLoading = rtkRequestStatusCodes.rejected;
    },
    [getEmailsVersions.fulfilled]: (state, action) => {
      state.emailsVersions = action.payload;
      state.emailsVersionsLoading = rtkRequestStatusCodes.fulfilled;
    },
    [getEmailsVersions.pending]: (state) => {
      state.emailsVersionsLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountOrganization.rejected]: (state) => {
      state.accountOrganizationLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountOrganization.fulfilled]: (state, action) => {
      state.accountOrganization = action.payload;
      state.accountOrganizationLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchAccountOrganization.pending]: (state) => {
      state.accountOrganizationLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountStatusById.rejected]: (state) => {
      state.accountStatusLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountStatusById.fulfilled]: (state, action) => {
      state.accountStatus = action.payload;
      state.accountStatusLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchAccountStatusById.pending]: (state) => {
      state.accountStatusLoading = rtkRequestStatusCodes.pending;
    },
    [updateAccountProperty.pending]: (state) => {
      state.accountDirectPropertiesLoading = rtkRequestStatusCodes.pending;
    },
    [updateAccountProperty.rejected]: (state) => {
      state.accountDirectPropertiesLoading = rtkRequestStatusCodes.idle; // Флаг только для индикации загрузки, поэтому по rejected сброс в idle
    },
    [fetchAccountDirectProperties.pending]: (state) => {
      state.accountDirectPropertiesLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountDirectProperties.fulfilled]: (state, action) => {
      state.accountDirectProperties = action.payload;
      state.accountDirectPropertiesLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchAccountDirectProperties.rejected]: (state) => {
      state.accountDirectPropertiesLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountPropertiesByOrgId.rejected]: (state) => {
      state.accountPropertiesLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountPropertiesByOrgId.fulfilled]: (state, action) => {
      state.accountProperties = action.payload;
      state.accountPropertiesLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchAccountPropertiesByOrgId.pending]: (state) => {
      state.accountPropertiesLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountTypePropertyVersions.rejected]: (state) => {
      state.accountTypePropertyVersionsLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountTypePropertyVersions.fulfilled]: (state, action) => {
      state.accountTypePropertyVersions = action.payload;
      state.accountTypePropertyVersionsLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchAccountTypePropertyVersions.pending]: (state) => {
      state.accountTypePropertyVersionsLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountAddressesByAddIds.rejected]: (state) => {
      state.accountAddressesLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountAddressesByAddIds.fulfilled]: (state, action) => {
      state.accountAddresses = action.payload;
      state.accountAddressesLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchAccountAddressesByAddIds.pending]: (state) => {
      state.accountAddressesLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountEmails.rejected]: (state) => {
      state.accountEmailsLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountEmails.fulfilled]: (state, action) => {
      state.accountEmails = action.payload;
      state.accountEmailsLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchAccountEmails.pending]: (state) => {
      state.accountEmailsLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountPhonesVersions.pending]: (state) => {
      state.accountPhonesVersionsLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountPhonesVersions.rejected]: (state) => {
      state.accountPhonesVersionsLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountPhonesVersions.fulfilled]: (state, { payload }) => {
      state.accountPhonesVersionsLoading = rtkRequestStatusCodes.fulfilled;
      state.accountPhonesVersions = payload;
    },
    [fetchAccountPhones.pending]: (state) => {
      state.accountPhonesLoading = rtkRequestStatusCodes.pending;
    },
    [fetchAccountPhones.rejected]: (state) => {
      state.accountPhonesLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchAccountPhones.fulfilled]: (state, { payload }) => {
      state.accountPhonesLoading = rtkRequestStatusCodes.fulfilled;
      const isIndividual = payload.individual !== null;
      if (isIndividual) {
        state.accountPhones = payload.individual.phones;
      } else {
        state.accountPhones = payload.organization.phones;
      }
    },
    [fetchIndividualByAccountId.pending]: (state) => {
      state.accountIndividualLoading = rtkRequestStatusCodes.pending;
    },
    [fetchIndividualByAccountId.rejected]: (state) => {
      state.accountIndividualLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchIndividualByAccountId.fulfilled]: (state, { payload }) => {
      state.accountIndividualLoading = rtkRequestStatusCodes.fulfilled;
      state.accountIndividual = payload.individual;
    },
    [fetchIndividualDocument.pending]: (state) => {
      state.accountIdentityDocumentsLoading = rtkRequestStatusCodes.pending;
    },
    [fetchIndividualDocument.rejected]: (state) => {
      state.accountIdentityDocumentsLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchIndividualDocument.fulfilled]: (state, { payload }) => {
      state.accountIdentityDocumentsLoading = rtkRequestStatusCodes.fulfilled;
      state.accountIdentityDocuments = payload.individual.identityDocuments;
    },
  },
});

const { reducer, actions } = accountsSlice;

const { selectAccId, setIsPageableSearch, cleanAccountSelect } = actions;

export {
  cleanAccountSelect,
  fetchAccounts,
  fetchAccountById,
  fetchAccountPageableSearch,
  selectAccId,
  setIsPageableSearch,
  getSubscribers,
  getOrganizationsVersions,
  getAccountsVersions,
  getLegalAddressVersions,
  getActualAddressVersions,
  getPropertiesVersions,
  fetchAccountsByName,
  getEmailsVersions,
  fetchAccountOrganization,
  fetchAccountStatusById,
  fetchAccountPropertiesByOrgId,
  fetchAccountAddressesByAddIds,
  fetchAccountEmails,
  fetchAccountPhonesVersions,
  fetchAccountByIdForBlocking,
  reducer,
};
